import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Check, X, Move } from "lucide-react";
import { SaasSettingsType } from "../components/dataTypes";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";

interface ToggleProps {
  checked: boolean;
  onChange: () => void;
}

const ToggleSwitch: React.FC<ToggleProps> = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`${
      checked ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-700"
    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer 
      rounded-full border-2 border-transparent transition-colors duration-200 
      ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`}
  >
    <span
      aria-hidden="true"
      className={`${
        checked ? "translate-x-5" : "translate-x-0"
      } inline-block h-5 w-5 transform rounded-full 
      bg-white dark:bg-gray-200 shadow ring-0 transition`}
    />
  </button>
);
interface SettingsPageProps {
  saadSettings: SaasSettingsType | null;
  handleRefresh: () => void;
}
const DowngradeSettings: React.FC<SettingsPageProps> = ({
  saadSettings,
  handleRefresh,
}) => {
  const AppServices = useAppServices();
  const { user } = useUserInfo();
  const [allowDowngrade, setAllowDowngrade] = useState(true);
  const [newReason, setNewReason] = useState("");

  const [reasons, setReasons] = useState<string[]>([]);

  // Editing state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  // Drag-and-drop
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    setAllowDowngrade(saadSettings?.downgrade?.allowDowngrade ?? true);
    setReasons(
      saadSettings?.downgrade?.reasons ?? [
        "Too expensive / Not enough value for money",
        "Not using the platform enough",
        "Switched to another tool",
        "Difficult to use / Complicated UI",
        "Was just exploring / No longer needed",
        "Not Interested",
      ]
    );
  }, [saadSettings]);

  // ---- Drag & drop handlers ----
  const handleDragStart = (index: number) => {
    if (!allowDowngrade) return;
    setDraggedIndex(index);
  };
  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...reasons];
    const [removed] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, removed);
    setReasons(updated);
    setDraggedIndex(index);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  // ---- Reason actions ----
  const handleAddReason = () => {
    if (!newReason.trim() || !allowDowngrade) return;
    setReasons([...reasons, newReason.trim()]);
    setNewReason("");
  };
  const handleDeleteReason = (index: number) => {
    if (!allowDowngrade) return;
    setReasons(reasons.filter((_, i) => i !== index));
  };
  const handleEditStart = (index: number, text: string) => {
    if (!allowDowngrade) return;
    setEditingIndex(index);
    setEditingText(text);
  };
  const handleEditSave = () => {
    if (editingIndex === null) return;
    const updated = [...reasons];
    updated[editingIndex] = editingText;
    setReasons(updated);
    setEditingIndex(null);
    setEditingText("");
  };
  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingText("");
  };

  const handleSubmit = async () => {
    try {
      const payload: any = {
        downgrade: {
          allowDowngrade,
          reasons,
        },
      };

      if (saadSettings?._id) {
        payload._id = saadSettings._id;
        await AppServices.saas_setting.update({ payload, toaster: true });
      } else {
        payload.companyId = user?.companyId;
        await AppServices.saas_setting.create({ payload, toaster: true });
      }

      handleRefresh();
    } catch (err) {}
  };

  return (
    <div className="p-8">
      {/* --- Top Blue Banner --- */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-5 flex items-center justify-between mb-8">
        <div>
          <h2 className="text-sm font-medium text-slate-900 dark:text-white">
            Allow clients (account admins) to downgrade their subscription.
          </h2>
          <a
            href="#"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            How does it work?
          </a>
        </div>

        <ToggleSwitch
          checked={allowDowngrade}
          onChange={() => setAllowDowngrade(!allowDowngrade)}
        />
      </div>

      {/* --- Header --- */}
      <div className="mb-6">
        <h1 className="text-lg font-medium text-slate-900 dark:text-white">
          Downgrade Plan Settings
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Configure how customers can downgrade their plan and what offers they
          receive
        </p>
      </div>

      {/* --- Main White Card --- */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6">
        {/* --- Manage Downgrade Plan Reason --- */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-4">
            Manage Downgrade Plan Reason
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {reasons.map((reason, index) => (
              <div
                key={index}
                draggable={allowDowngrade}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className="group flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-md px-4 py-3 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
              >
                {allowDowngrade && (
                  <Move className="h-4 w-4 mr-2 cursor-grab text-gray-400" />
                )}

                {editingIndex === index ? (
                  <input
                    type="text"
                    value={editingText}
                    disabled={!allowDowngrade}
                    maxLength={50}
                    onChange={(e) => setEditingText(e.target.value)}
                    className="w-full text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border px-2 py-1 rounded"
                  />
                ) : (
                  <span
                    className={`text-sm truncate pr-4 ${
                      allowDowngrade
                        ? "text-slate-700 dark:text-gray-300"
                        : "text-gray-400 dark:text-gray-600"
                    }`}
                  >
                    {reason}
                  </span>
                )}

                <div className="flex items-center gap-3 opacity-60 group-hover:opacity-100 transition-opacity">
                  {editingIndex === index ? (
                    <>
                      <button
                        onClick={handleEditSave}
                        disabled={!allowDowngrade}
                        className="text-green-600 disabled:opacity-30"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditStart(index, reason)}
                        disabled={!allowDowngrade}
                        className="text-gray-400 dark:text-gray-300 hover:text-blue-600 disabled:opacity-30"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteReason(index)}
                        disabled={!allowDowngrade}
                        className="text-gray-400 dark:text-gray-300 hover:text-red-500 disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Other Reason */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-md px-4 py-3 bg-white dark:bg-gray-800 mb-6">
            <span className="text-sm text-slate-700 dark:text-gray-300">
              Other
            </span>
          </div>

          {/* Add New Reason */}
          <div className="flex items-center gap-3 mb-2">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Add New Reason"
                value={newReason}
                maxLength={50}
                onChange={(e) => setNewReason(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-md py-2 px-3 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">
                {newReason.length} / 50
              </span>
            </div>

            <button
              onClick={handleAddReason}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            "Other" is always included for open-ended input.
          </p>
        </div>

        {/* --- Footer --- */}
        <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
          <button className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition">
            Preview
          </button>

          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DowngradeSettings;
